import React, { useState, useEffect, useRef } from "react";
import client from "../../../shared/api/client";
import MaskedInput from "react-text-mask";
import createNumberMask from "text-mask-addons/dist/createNumberMask";
import MdEditor from "react-markdown-editor-lite";
import { COUNTRY_OPTIONS } from "../../CustomFieldValues/constants/customFieldValuesConstants";
import { toastSuccess, toastError } from "../../../shared/toastHelper";
import { isValidPhone, isValidEmailField } from "../../../shared/validationUtils";
import { formatPhone } from "../../../shared/formattingUtils";
import { uploadImageEmbed } from "../../VenueManager/components/VenueManager";
import ImageIcon from "../../shared/Icons/ImageIcon";
import XCloseIcon from "../../shared/Icons/XCloseIcon";
import "../../TeamManager/styles/teamManagerStyles.css";

var emoji = require("markdown-it-emoji");
var taskLists = require("markdown-it-task-lists");

const mdParser = require("markdown-it")()
  .use(require("markdown-it-mark"))
  .use(require("markdown-it-ins"))
  .use(require("markdown-it-abbr"))
  .use(require("markdown-it-deflist"))
  .use(require("markdown-it-footnote"))
  .use(require("markdown-it-sub"))
  .use(require("markdown-it-sup"))
  .use(taskLists)
  .use(emoji);

const PHONE_MASK = [
  "(", /[1-9]/, /\d/, /\d/, ")", " ", /\d/, /\d/, /\d/, "-", /\d/, /\d/, /\d/, /\d/,
];

const CAPACITY_MASK = createNumberMask({
  prefix: "",
  suffix: "",
  includeThousandsSeparator: true,
  thousandsSeparatorSymbol: ",",
  allowDecimal: false,
  allowNegative: false,
  allowLeadingZeroes: false,
  integerLimit: 7,
});


const MD_PLUGINS = [
  "header",
  "font-bold",
  "font-italic",
  "font-underline",
  "font-strikethrough",
  "list-unordered",
  "list-ordered",
  "block-quote",
  "block-wrap",
  "table",
  "image",
  "link",
  "clear",
  "logger",
  "mode-toggle",
  "full-screen",
  "tab-insert",
];

const LogoCircle = ({ preview }) => (
  <div
    style={{
      width: 80,
      height: 80,
      borderRadius: "50%",
      overflow: "hidden",
      flexShrink: 0,
      border: "1px solid var(--sl-border-secondary, #e9eaeb)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      backgroundColor: "var(--sl-bg-tertiary, #f5f5f5)",
    }}
  >
    {preview ? (
      <img src={preview} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
    ) : (
      <ImageIcon width="24" height="24" fill="var(--sl-text-quaternary, #717680)" />
    )}
  </div>
);

const VenueFormModal = ({
  isOpen,
  onClose,
  csrfToken,
  team,
  venueStateOptions,
  availableTimezones,
  onVenueAdded,
  venueOwnership,
  onVenueUpdated,
}) => {
  const isEditMode = !!venueOwnership;

  const [name, setName] = useState("");
  const [address1, setAddress1] = useState("");
  const [address2, setAddress2] = useState("");
  const [city, setCity] = useState("");
  const [stateCode, setStateCode] = useState("");
  const [zip, setZip] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [website, setWebsite] = useState("");
  const [capacity, setCapacity] = useState("");
  const [country, setCountry] = useState("");
  const [timezone, setTimezone] = useState("");
  const [bio, setBio] = useState("");
  const [logo, setLogo] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [ticketLogo, setTicketLogo] = useState(null);
  const [ticketLogoPreview, setTicketLogoPreview] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const logoInputRef = useRef(null);
  const ticketLogoInputRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return;

    if (isEditMode) {
      setName(venueOwnership.name || venueOwnership.venueable.name || "");
      setAddress1(venueOwnership.address1 || "");
      setAddress2(venueOwnership.address2 || "");
      setCity(venueOwnership.city || "");
      setStateCode(venueOwnership.state_code_or_name || "");
      setZip(venueOwnership.zip || "");
      setPhone(formatPhone(venueOwnership.phone || ""));
      setEmail(venueOwnership.email || "");
      setWebsite(venueOwnership.website || "");
      setCapacity(venueOwnership.capacity != null ? String(venueOwnership.capacity) : "");
      setCountry(venueOwnership.country_code || "");
      setTimezone(venueOwnership.time_zone || "");
      setBio(venueOwnership.bio || "");
      setLogoPreview(venueOwnership.logo_thumb_url || null);
      setTicketLogoPreview(venueOwnership.ticket_logo_thumb_url || null);
    } else {
      setName("");
      setAddress1("");
      setAddress2("");
      setCity("");
      setStateCode("");
      setZip("");
      setPhone("");
      setEmail("");
      setWebsite("");
      setCapacity("");
      setCountry("US");
      setTimezone("");
      setBio("");
      setLogoPreview(null);
      setTicketLogoPreview(null);
    }
    setLogo(null);
    setTicketLogo(null);
    setSubmitting(false);
  }, [isOpen]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape" && isOpen) onClose();
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const canSubmit =
    name.trim().length > 0 &&
    address1.trim().length > 0 &&
    city.trim().length > 0 &&
    stateCode.length > 0 &&
    zip.trim().length > 0 &&
    timezone.length > 0 &&
    isValidPhone(phone) &&
    isValidEmailField(email);

  const buildFormData = () => {
    var params = new FormData();
    params.append("venue_ownership[time_zone]", timezone);
    params.append("venue_ownership[address1]", address1.trim());
    params.append("venue_ownership[address2]", address2.trim());
    params.append("venue_ownership[city]", city.trim());
    params.append("venue_ownership[state_code]", stateCode);
    params.append("venue_ownership[zip]", zip.trim());

    if (bio && bio.length > 0) {
      params.set("venue_ownership[bio]", bio);
    } else {
      params.set("venue_ownership[bio]", "");
    }

    if (phone && phone.length > 0) {
      params.set("venue_ownership[phone]", phone.replace(/\D/g, ""));
    } else {
      params.set("venue_ownership[phone]", "");
    }

    if (email && email.length > 0) {
      params.set("venue_ownership[email]", email);
    } else {
      params.set("venue_ownership[email]", "");
    }

    if (isEditMode && name.trim() !== venueOwnership.name) {
      params.set("venue_ownership[nickname]", name.trim());
    }

    params.set("venue_ownership[website]", website.trim());
    params.set("venue_ownership[capacity]", String(capacity).replace(/,/g, ""));

    const selectedCountry = COUNTRY_OPTIONS.find((c) => c.code === country);
    params.set("venue_ownership[country_code]", country);
    params.set("venue_ownership[country_name]", selectedCountry ? selectedCountry.name : "");

    if (logo) {
      params.append("venue_ownership[logo]", logo);
    }

    if (ticketLogo) {
      params.append("venue_ownership[ticket_logo]", ticketLogo);
    }

    return params;
  };

  const handleSubmit = async () => {
    if (!canSubmit || submitting) return;
    setSubmitting(true);

    try {
      if (isEditMode) {
        const formData = buildFormData();
        const res = await client.patch(
          `/teams/${team.id}/venue_ownerships/${venueOwnership.id}`,
          formData,
          { headers: { "Content-Type": "multipart/form-data" } }
        );
        onVenueUpdated(res.data);
        toastSuccess("Venue updated successfully.");
      } else {
        const venueRes = await client.post("/venues", {
          venue: {
            name: name.trim(),
            address1: address1.trim(),
            address2: address2.trim(),
            city: city.trim(),
            state_code: stateCode,
            zip: zip.trim(),
          },
        });
        const venueId = venueRes.data.id;

        const ownershipRes = await client.post(
          `/teams/${team.id}/venue_ownerships`,
          {
            venue_ownership: {
              venueable_id: venueId,
              venueable_type: "Venue",
            },
          }
        );
        const ownershipId = ownershipRes.data.id;

        const formData = buildFormData();
        const updateRes = await client.patch(
          `/teams/${team.id}/venue_ownerships/${ownershipId}`,
          formData,
          { headers: { "Content-Type": "multipart/form-data" } }
        );

        onVenueAdded(updateRes.data);
        toastSuccess("Venue added successfully.");
      }
      onClose();
    } catch (error) {
      var message = "Something went wrong. Please try again.";
      if (error.response && error.response.data) {
        var errors = error.response.data;
        if (Array.isArray(errors) && errors.length > 0) {
          message = errors.join(", ");
        } else if (typeof errors === "string") {
          message = errors;
        }
      }
      toastError(message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleLogoChange = (e) => {
    var file = e.target.files[0];
    if (file) {
      setLogo(file);
      setLogoPreview(URL.createObjectURL(file));
    }
    e.target.value = null;
  };

  const handleTicketLogoChange = (e) => {
    var file = e.target.files[0];
    if (file) {
      setTicketLogo(file);
      setTicketLogoPreview(URL.createObjectURL(file));
    }
    e.target.value = null;
  };

  const removeLogo = () => {
    setLogo(null);
    setLogoPreview(null);
  };

  const removeTicketLogo = () => {
    setTicketLogo(null);
    setTicketLogoPreview(null);
  };

  return (
    <div className="team-modal-overlay">
      <style>{`
        html[data-theme="dark"] body.od-themed .venue-bio-editor.rc-md-editor {
          border-color: var(--sl-border-secondary);
          background-color: var(--sl-bg-primary);
          color: var(--sl-text-primary);
        }
        html[data-theme="dark"] body.od-themed .venue-bio-editor .rc-md-navigation {
          background-color: var(--sl-bg-secondary);
          border-bottom-color: var(--sl-border-secondary);
          color: var(--sl-text-primary);
        }
        html[data-theme="dark"] body.od-themed .venue-bio-editor .rc-md-navigation .button {
          color: var(--sl-text-secondary);
        }
        html[data-theme="dark"] body.od-themed .venue-bio-editor .editor-container .sec-md .input {
          background-color: var(--sl-bg-primary);
          color: var(--sl-text-primary);
        }
        html[data-theme="dark"] body.od-themed .venue-bio-editor .drop-wrap {
          background-color: var(--sl-bg-secondary);
          border-color: var(--sl-border-secondary);
          color: var(--sl-text-primary);
        }
      `}</style>
      <div
        className="team-modal"
        style={{ maxWidth: 800, width: "90%", padding: 0 }}
      >
        <div className="team-modal-header">
          <h3 className="team-modal-title">
            {isEditMode ? "Edit Venue" : "Add a Venue"}
          </h3>
          <button className="team-modal-close" onClick={onClose}>
            <XCloseIcon width="20" height="20" />
          </button>
        </div>

        <div className="team-modal-body">
            <div className="team-form-group">
              <label className="team-form-label">
                Venue Name <span style={{ color: "var(--sl-text-error, #e63946)" }}>*</span>
              </label>
              <input
                type="text"
                className="team-form-input"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Venue name"
              />
            </div>

            <div className="team-form-group">
              <label className="team-form-label">Country</label>
              <select
                className="team-form-select"
                value={country}
                onChange={(e) => setCountry(e.target.value)}
              >
                <option value="">Select a country</option>
                {COUNTRY_OPTIONS.map((c) => (
                  <option key={c.code} value={c.code}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="team-form-group">
              <label className="team-form-label">
                Address 1 <span style={{ color: "var(--sl-text-error, #e63946)" }}>*</span>
              </label>
              <input
                type="text"
                className="team-form-input"
                value={address1}
                onChange={(e) => setAddress1(e.target.value)}
                placeholder="Street address"
              />
            </div>

            <div className="team-form-group">
              <label className="team-form-label">Address 2</label>
              <input
                type="text"
                className="team-form-input"
                value={address2}
                onChange={(e) => setAddress2(e.target.value)}
                placeholder="Suite, unit, etc."
              />
            </div>

            <div className="team-form-row--3col">
              <div className="team-form-group">
                <label className="team-form-label">
                  City <span style={{ color: "var(--sl-text-error, #e63946)" }}>*</span>
                </label>
                <input
                  type="text"
                  className="team-form-input"
                  value={city}
                  onChange={(e) => setCity(e.target.value)}
                  placeholder="City"
                />
              </div>
              <div className="team-form-group">
                <label className="team-form-label">
                  State <span style={{ color: "var(--sl-text-error, #e63946)" }}>*</span>
                </label>
                <select
                  className="team-form-select"
                  value={stateCode}
                  onChange={(e) => setStateCode(e.target.value)}
                >
                  <option value="">Select</option>
                  {(venueStateOptions || []).map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
              </div>
              <div className="team-form-group">
                <label className="team-form-label">
                  Postal Code <span style={{ color: "var(--sl-text-error, #e63946)" }}>*</span>
                </label>
                <input
                  type="text"
                  className="team-form-input"
                  value={zip}
                  onChange={(e) => setZip(e.target.value)}
                  placeholder="Zip"
                />
              </div>
            </div>

            <div className="team-form-row--3col">
              <div className="team-form-group">
                <label className="team-form-label">
                  Phone <span style={{ color: "var(--sl-text-error, #e63946)" }}>*</span>
                </label>
                <MaskedInput
                  className="team-form-input"
                  placeholder="(317) 867-5309"
                  guide={false}
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  mask={PHONE_MASK}
                />
                {phone && !isValidPhone(phone) && (
                  <div className="team-form-error">Enter a valid 10-digit phone number</div>
                )}
              </div>
              <div className="team-form-group">
                <label className="team-form-label">
                  Email <span style={{ color: "var(--sl-text-error, #e63946)" }}>*</span>
                </label>
                <input
                  type="email"
                  className="team-form-input"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="support@venue.com"
                />
                {email && !isValidEmailField(email) && (
                  <div className="team-form-error">Enter a valid email address</div>
                )}
              </div>
              <div className="team-form-group">
                <label className="team-form-label">
                  Timezone <span style={{ color: "var(--sl-text-error, #e63946)" }}>*</span>
                </label>
                <select
                  className="team-form-select"
                  value={timezone}
                  onChange={(e) => setTimezone(e.target.value)}
                >
                  <option value="">Select timezone</option>
                  {(availableTimezones || []).map((tz) => (
                    <option key={tz} value={tz}>
                      {tz}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="team-form-row--2col">
              <div className="team-form-group">
                <label className="team-form-label">Website</label>
                <input
                  type="text"
                  className="team-form-input"
                  value={website}
                  onChange={(e) => setWebsite(e.target.value)}
                  placeholder="venue.com"
                />
              </div>
              <div className="team-form-group">
                <label className="team-form-label">Capacity</label>
                <MaskedInput
                  className="team-form-input"
                  mask={CAPACITY_MASK}
                  guide={false}
                  inputMode="numeric"
                  value={capacity}
                  onChange={(e) => setCapacity(e.target.value)}
                  placeholder="500"
                />
              </div>
            </div>

            <div className="team-form-row--2col">
              <div className="team-form-group">
                <label className="team-form-label">Venue Logo</label>
                <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
                  <LogoCircle preview={logoPreview} />
                  <div>
                    <p
                      style={{
                        fontSize: 12,
                        color: "var(--sl-text-tertiary, #535862)",
                        margin: "0 0 8px 0",
                      }}
                    >
                      Images must be at least 200 x 200px
                    </p>
                    <input
                      ref={logoInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleLogoChange}
                      style={{ display: "none" }}
                    />
                    <button
                      type="button"
                      className="team-btn-secondary"
                      style={{ padding: "6px 12px", fontSize: 13 }}
                      onClick={() => logoInputRef.current.click()}
                    >
                      Choose Photo
                    </button>
                    {logoPreview && (
                      <button
                        type="button"
                        onClick={removeLogo}
                        style={{
                          background: "none",
                          border: "none",
                          color: "var(--sl-text-error, #e63946)",
                          fontSize: 13,
                          cursor: "pointer",
                          marginLeft: 8,
                        }}
                      >
                        Remove
                      </button>
                    )}
                  </div>
                </div>
              </div>

              <div className="team-form-group">
                <label className="team-form-label">Ticket Logo</label>
                <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
                  <LogoCircle preview={ticketLogoPreview} />
                  <div>
                    <p
                      style={{
                        fontSize: 12,
                        color: "var(--sl-text-tertiary, #535862)",
                        margin: "0 0 8px 0",
                      }}
                    >
                      Displayed in black and white, at 100x100px
                    </p>
                    <input
                      ref={ticketLogoInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleTicketLogoChange}
                      style={{ display: "none" }}
                    />
                    <button
                      type="button"
                      className="team-btn-secondary"
                      style={{ padding: "6px 12px", fontSize: 13 }}
                      onClick={() => ticketLogoInputRef.current.click()}
                    >
                      Choose Photo
                    </button>
                    {ticketLogoPreview && (
                      <button
                        type="button"
                        onClick={removeTicketLogo}
                        style={{
                          background: "none",
                          border: "none",
                          color: "var(--sl-text-error, #e63946)",
                          fontSize: 13,
                          cursor: "pointer",
                          marginLeft: 8,
                        }}
                      >
                        Remove
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="team-form-group" style={{ marginBottom: 0 }}>
              <label className="team-form-label">Venue Bio</label>
              <MdEditor
                className="venue-bio-editor"
                style={{ height: "300px" }}
                view={{ menu: true, md: true, html: false }}
                plugins={MD_PLUGINS}
                onImageUpload={(file, callback) => {
                  uploadImageEmbed(csrfToken, team, file, callback);
                }}
                renderHTML={(text) => mdParser.render(text)}
                value={bio}
                onChange={({ text }) => setBio(text)}
              />
            </div>
        </div>

        <div className="team-modal-footer">
          <button className="team-btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button
            className="team-btn-primary"
            disabled={!canSubmit || submitting}
            onClick={handleSubmit}
            style={
              !canSubmit || submitting
                ? { opacity: 0.4, cursor: "not-allowed" }
                : {}
            }
          >
            {submitting ? "Saving..." : isEditMode ? "Save Changes" : "Add Venue"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default VenueFormModal;
